import { getPushPublicKey, subscribePush, unsubscribePush } from "@/api/account";
import { isNativeApp } from "@/lib/runtime";
import {
  ensureNativePushSubscribed,
  ensureNativePushUnsubscribed,
  getNativePushPermission,
  syncNativePushRegistration,
} from "@/lib/nativePushNotifications";
import { getOrCreateInstallationId } from "@/lib/installationId";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replaceAll("-", "+").replaceAll("_", "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function getOrCreatePushInstallationId() {
  return getOrCreateInstallationId();
}

export function getPushEnvironment() {
  if (isNativeApp()) {
    return { sw: true, notif: true, push: true, https: true, native: true };
  }
  if (typeof window === "undefined") {
    return { sw: false, notif: false, push: false, https: false };
  }

  return {
    sw: "serviceWorker" in navigator,
    notif: "Notification" in window,
    push: "PushManager" in window,
    https: window.isSecureContext || window.location.hostname === "localhost",
  };
}

export async function ensurePushSubscribed() {
  if (isNativeApp()) {
    const result = await ensureNativePushSubscribed();
    window.dispatchEvent(new CustomEvent("push:permission-changed"));
    return result;
  }
  const env = getPushEnvironment();

  if (!env.https) throw new Error("Push kräver HTTPS (eller localhost).");
  if (!env.sw || !env.notif || !env.push) throw new Error("Webbläsaren saknar push-stöd.");

  if (Notification.permission === "denied") throw new Error("Notiser är blockerade. Tillåt dem i webbläsarens eller enhetens inställningar och försök igen.");
  if (Notification.permission === "default") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") throw new Error("Du behöver tillåta notiser.");
  }

  await navigator.serviceWorker.register("/sw.js");
  const ready = await navigator.serviceWorker.ready;
  let sub = await ready.pushManager.getSubscription();
  if (!sub) {
    const { vapidPublicKey } = await getPushPublicKey();
    sub = await ready.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  const json = sub.toJSON();
  await subscribePush({
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    installation_id: getOrCreatePushInstallationId(),
  });
}

export async function getPushPermission() {
  if (isNativeApp()) return getNativePushPermission();
  return typeof Notification === "undefined" ? "default" : Notification.permission;
}

export async function syncExistingPushSubscription() {
  if (isNativeApp()) return syncNativePushRegistration();
  const env = getPushEnvironment();
  if (!env.https || !env.sw || !env.notif || !env.push) return false;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return false;
  }

  await navigator.serviceWorker.register("/sw.js");
  const ready = await navigator.serviceWorker.ready;
  const sub = await ready.pushManager.getSubscription();
  if (!sub) return false;

  const json = sub.toJSON();
  await subscribePush({
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    installation_id: getOrCreatePushInstallationId(),
  });
  return true;
}

export async function ensurePushUnsubscribed() {
  if (isNativeApp()) return ensureNativePushUnsubscribed();
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  const ready = await navigator.serviceWorker.ready;
  const sub = await ready.pushManager.getSubscription();
  if (sub) {
    try {
      await unsubscribePush({
        endpoint: sub.endpoint,
        installation_id: getOrCreatePushInstallationId(),
      });
    } catch (_) {
      // ok om redan borta
    }
    await sub.unsubscribe();
  }
}

export function isPushSupported() {
  const env = getPushEnvironment();
  return env.https && env.sw && env.notif && env.push;
}
